import { Injectable, inject, NgZone } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { filter } from 'rxjs/operators';

/**
 * Service to handle native back button events for Android and iOS.
 *
 * Features:
 * - Automatically detects and closes open modals/overlays (no custom code needed)
 * - Navigates back in Angular router history
 * - Minimizes app when at root route (Android)
 * - Supports registering custom back handlers for special cases
 *
 * Usage:
 * - Inject in app.component.ts and call initialize() in ngOnInit
 * - Modals/overlays are automatically detected and closed via DOM inspection
 * - For special cases, components can register custom handlers
 */
@Injectable({
  providedIn: 'root',
})
export class BackButtonService {
  private router = inject(Router);
  private location = inject(Location);
  private ngZone = inject(NgZone);

  /** Stack of custom back handlers (for special cases only) */
  private customHandlers: Array<() => boolean> = [];

  /** Navigation history for proper back navigation */
  private navigationHistory: string[] = [];

  /** Maximum history length to track */
  private readonly MAX_HISTORY_LENGTH = 50;

  /** Root routes where back button should minimize/exit the app */
  private readonly ROOT_ROUTES = [
    '/offers',
    '/menus',
    '/places',
    '/liked-offers',
  ];

  /** Flag to prevent double-tap exit */
  private lastBackPress = 0;
  private readonly DOUBLE_TAP_THRESHOLD = 2000; // 2 seconds

  /**
   * Selectors for modal/overlay backdrops that should be closed on back press.
   * These are checked in order - first match wins.
   */
  private readonly MODAL_BACKDROP_SELECTORS = [
    // Fixed overlays with backdrop blur or dark background
    '.fixed.inset-0.backdrop-blur-md',
    '.fixed.inset-0.backdrop-blur',
    '.fixed.inset-0.bg-black\\/30',
    '.fixed.inset-0.bg-black\\/50',
    // Generic fixed overlays with z-index (likely modals)
    '.fixed.inset-0[class*="z-"]',
    // Common modal patterns
    '.modal-backdrop',
    '.overlay-backdrop',
    '.sidebar-overlay',
    // DaisyUI modals
    '.modal.modal-open',
    '.modal-box',
    // Flowbite modals
    '[data-modal-backdrop]',
  ];

  /**
   * Selectors for close buttons inside modals (fallback)
   */
  private readonly CLOSE_BUTTON_SELECTORS = [
    '[aria-label="Cerrar"]',
    '[aria-label="Close"]',
    'button.btn-close',
    'button[class*="close"]',
    '.modal-close',
  ];

  /** Prevents re-entrant modal close during history.back() popstate cascade */
  private isHandlingModalClose = false;

  /**
   * Initialize the back button listener.
   * Should be called once in app.component.ts ngOnInit.
   */
  async initialize(): Promise<void> {
    // Always track navigation history (needed by pushModalState on all platforms)
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.addToHistory(event.urlAfterRedirects);
      });

    if (!Capacitor.isNativePlatform()) {
      console.log(
        'BackButtonService: Not a native platform, skipping native init',
      );
      return;
    }

    console.log('BackButtonService: Initializing native back button handler');

    // Dynamically import @capacitor/app to avoid build errors if not installed
    try {
      const { App } = await import('@capacitor/app');

      // Listen to native back button events
      App.addListener('backButton', ({ canGoBack }: { canGoBack: boolean }) => {
        // Run inside Angular zone to ensure change detection works
        this.ngZone.run(() => {
          this.handleBackButton(canGoBack);
        });
      });

      console.log('BackButtonService: Back button listener registered');
    } catch (error) {
      console.warn(
        'BackButtonService: @capacitor/app not installed. Run: pnpm add @capacitor/app',
      );
    }
  }

  /**
   * Push a modal state onto browser history so that back gestures (iOS swipe,
   * Android gesture/button) close the modal instead of navigating away.
   *
   * Call this when opening a full-screen modal or overlay.
   * Returns a cleanup function that MUST be called when the modal closes
   * programmatically (close button, backdrop tap, navigation away, etc.).
   *
   * @param onClose  Callback invoked when the user gestures back while
   *                 the modal is open. Should close the modal UI.
   * @returns        Cleanup function to call on programmatic close.
   */
  pushModalState(onClose: () => void): () => void {
    let active = true;

    // Push a browser history entry so iOS gesture back has something to pop
    history.pushState({ __modal: true }, '');

    // Register a custom back handler for Android (Capacitor backButton)
    const unregisterHandler = this.registerHandler(() => {
      if (!active) return false;
      active = false;
      window.removeEventListener('popstate', onPopstate);
      this.isHandlingModalClose = true;
      onClose();
      history.back();
      setTimeout(() => { this.isHandlingModalClose = false; }, 100);
      return true;
    });

    // Listen for popstate — fires when iOS swipe-back pops our { __modal } entry
    const onPopstate = () => {
      if (!active || this.isHandlingModalClose) return;
      active = false;
      unregisterHandler();
      this.ngZone.run(() => onClose());
    };
    window.addEventListener('popstate', onPopstate);

    // Cleanup for programmatic close (close button, backdrop, etc.)
    return () => {
      if (!active) return;
      active = false;
      unregisterHandler();
      window.removeEventListener('popstate', onPopstate);
      this.isHandlingModalClose = true;
      history.back();
      setTimeout(() => { this.isHandlingModalClose = false; }, 100);
    };
  }

  /**
   * Register a custom back handler for special cases.
   * Returns a function to unregister the handler.
   *
   * Note: Most modals are handled automatically via DOM detection.
   * Only use this for components that need special behavior.
   */
  registerHandler(handler: () => boolean): () => void {
    this.customHandlers.push(handler);
    console.log(
      `BackButtonService: Handler registered, total handlers: ${this.customHandlers.length}`,
    );

    return () => {
      const index = this.customHandlers.indexOf(handler);
      if (index > -1) {
        this.customHandlers.splice(index, 1);
        console.log(
          `BackButtonService: Handler unregistered, remaining handlers: ${this.customHandlers.length}`,
        );
      }
    };
  }

  /**
   * Handle the back button press.
   *
   * Order: custom handlers → close modals → navigate back via history → root exit → fallback.
   * History navigation MUST come before the root check so that sub-navigation
   * within a root route (e.g. /offers?brandId=123 → /offers) works correctly
   * instead of prematurely exiting the app.
   */
  private handleBackButton(canGoBack: boolean): void {
    console.log(
      `BackButtonService: Back button pressed, canGoBack: ${canGoBack}`,
    );

    // 1. First, try custom handlers (LIFO - last registered, first called)
    for (let i = this.customHandlers.length - 1; i >= 0; i--) {
      const handler = this.customHandlers[i];
      try {
        if (handler()) {
          console.log(
            'BackButtonService: Custom handler handled the back action',
          );
          return;
        }
      } catch (error) {
        console.error('BackButtonService: Custom handler error:', error);
        this.customHandlers.splice(i, 1);
      }
    }

    // 2. Try to close any open modal/overlay automatically
    if (this.tryCloseModal()) {
      console.log('BackButtonService: Modal closed automatically');
      return;
    }

    // 3. Try to navigate back via history
    if (this.navigationHistory.length > 1) {
      console.log('BackButtonService: Navigating back via history');
      this.navigationHistory.pop();
      const previousUrl =
        this.navigationHistory[this.navigationHistory.length - 1];
      if (previousUrl) {
        this.router.navigateByUrl(previousUrl);
        return;
      }
    }

    // 4. No more history — check if at a root route to exit/minimize
    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    const isAtRoot = this.ROOT_ROUTES.some(
      (route) => currentUrl === route || currentUrl === '/',
    );

    if (isAtRoot) {
      this.handleAppExit();
      return;
    }

    // 5. Fallback: use Location.back() or navigate to default
    if (canGoBack) {
      console.log('BackButtonService: Using Location.back()');
      this.location.back();
    } else {
      console.log('BackButtonService: No history, navigating to offers');
      this.router.navigate(['/offers']);
    }
  }

  /**
   * Try to close any open modal/overlay by detecting them in the DOM.
   * Returns true if a modal was found and closed.
   */
  private tryCloseModal(): boolean {
    // Check if body has overflow-hidden (indicates modal is likely open)
    const bodyHasOverflowHidden =
      document.body.classList.contains('overflow-hidden');

    // Find the topmost modal backdrop by z-index
    const modalBackdrop = this.findTopmostModal();

    if (modalBackdrop) {
      console.log(
        'BackButtonService: Found modal backdrop, attempting to close',
      );

      // Strategy 1: Click the backdrop (most modals close on backdrop click)
      if (this.clickBackdrop(modalBackdrop)) {
        return true;
      }

      // Strategy 2: Find and click a close button within the modal
      if (this.clickCloseButton(modalBackdrop)) {
        return true;
      }
    }

    // Fallback: if body has overflow-hidden but no backdrop found,
    // try to find any visible close button
    if (bodyHasOverflowHidden) {
      console.log(
        'BackButtonService: Body has overflow-hidden, looking for close button',
      );
      return this.clickCloseButton(document.body);
    }

    return false;
  }

  /**
   * Find the topmost modal element by checking z-index
   */
  private findTopmostModal(): HTMLElement | null {
    let topmostModal: HTMLElement | null = null;
    let highestZIndex = 0;

    for (const selector of this.MODAL_BACKDROP_SELECTORS) {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          const el = element as HTMLElement;
          if (this.isElementVisible(el)) {
            const zIndex = this.getEffectiveZIndex(el);
            if (zIndex >= highestZIndex) {
              highestZIndex = zIndex;
              topmostModal = el;
            }
          }
        });
      } catch {
        // Invalid selector, skip
      }
    }

    return topmostModal;
  }

  /**
   * Get the effective z-index of an element (including parent z-indexes)
   */
  private getEffectiveZIndex(element: HTMLElement): number {
    const style = window.getComputedStyle(element);
    const zIndex = parseInt(style.zIndex, 10);
    return isNaN(zIndex) ? 0 : zIndex;
  }

  /**
   * Check if an element is visible
   */
  private isElementVisible(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      parseFloat(style.opacity) > 0
    );
  }

  /**
   * Click on a backdrop element to close the modal
   */
  private clickBackdrop(backdrop: HTMLElement): boolean {
    try {
      // Dispatch a click event on the backdrop
      const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      });

      backdrop.dispatchEvent(clickEvent);

      // Give Angular a moment to process
      return true;
    } catch (error) {
      console.error('BackButtonService: Error clicking backdrop:', error);
      return false;
    }
  }

  /**
   * Find and click a close button within a container
   */
  private clickCloseButton(container: HTMLElement): boolean {
    for (const selector of this.CLOSE_BUTTON_SELECTORS) {
      try {
        const closeButton = container.querySelector(selector) as HTMLElement;
        if (closeButton && this.isElementVisible(closeButton)) {
          console.log(`BackButtonService: Found close button: ${selector}`);
          closeButton.click();
          return true;
        }
      } catch {
        // Invalid selector, skip
      }
    }
    return false;
  }

  /**
   * Handle app exit when at root route.
   * Uses double-tap to exit pattern on Android.
   */
  private async handleAppExit(): Promise<void> {
    const now = Date.now();

    if (now - this.lastBackPress < this.DOUBLE_TAP_THRESHOLD) {
      console.log('BackButtonService: Double tap detected, minimizing app');
      try {
        const { App } = await import('@capacitor/app');
        App.minimizeApp();
      } catch {
        console.warn('BackButtonService: Could not minimize app');
      }
    } else {
      this.lastBackPress = now;
      console.log(
        'BackButtonService: First tap at root, waiting for second tap to exit',
      );
      this.showExitToast();
    }
  }

  /**
   * Show a toast message indicating double-tap to exit.
   */
  private showExitToast(): void {
    this.createNativeToast('Presiona de nuevo para salir');
  }

  /**
   * Create a simple native-style toast notification.
   */
  private createNativeToast(message: string): void {
    const existingToast = document.querySelector('.native-back-toast');
    if (existingToast) {
      existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.className = 'native-back-toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 100px;
      left: 50%;
      transform: translateX(-50%);
      background-color: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 24px;
      border-radius: 24px;
      font-size: 14px;
      z-index: 10000;
      animation: fadeInOut 2s ease-in-out forwards;
    `;

    if (!document.querySelector('#native-back-toast-styles')) {
      const style = document.createElement('style');
      style.id = 'native-back-toast-styles';
      style.textContent = `
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateX(-50%) translateY(20px); }
          15% { opacity: 1; transform: translateX(-50%) translateY(0); }
          85% { opacity: 1; transform: translateX(-50%) translateY(0); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-20px); }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 2000);
  }

  /**
   * Add a URL to the navigation history.
   */
  private addToHistory(url: string): void {
    if (this.navigationHistory[this.navigationHistory.length - 1] === url) {
      return;
    }

    this.navigationHistory.push(url);

    if (this.navigationHistory.length > this.MAX_HISTORY_LENGTH) {
      this.navigationHistory = this.navigationHistory.slice(
        -this.MAX_HISTORY_LENGTH,
      );
    }

    console.log(
      `BackButtonService: History updated, length: ${this.navigationHistory.length}`,
    );
  }

  /**
   * Clear the navigation history.
   */
  clearHistory(): void {
    this.navigationHistory = [];
    console.log('BackButtonService: History cleared');
  }

  /**
   * Get the current navigation history (for debugging).
   */
  getHistory(): string[] {
    return [...this.navigationHistory];
  }
}
